import {
  FormControl,
  Input,
  Box,
  Flex,
  Text,
  Collapse,
  Link,
  FormErrorMessage,
} from '@chakra-ui/react';
import {
  setBeaconProp,
  removeBeacon,
} from '../../../store/redux/slices/template.slice';
import { useAppDispatch } from '../../../store/redux/hooks';
import { BeaconInput } from '../../../store/types/template';
import { useTemplateFieldValidation } from './validation/useTemplateFieldValidation';
import { ChevronDownIcon, ChevronLeftIcon } from '@chakra-ui/icons';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useAppSelector } from '../../../store/redux/hooks';
import { LabelWithTooltip } from './LabelWithTooltip';
import FieldEditIndicator from './FieldEditIndicator';
import { useFieldEditIndicator } from './hooks/useFieldEditIndicator';
import { CustomErrorIcon } from '../../../theme/icons/CustomErrorIcon';

const BeaconWrapper = ({
  beacon,
  index,
  newlyAdded = false,
}: {
  beacon: BeaconInput;
  index: number;
  newlyAdded?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const { validateField, handleFocus, handleBlur } =
    useTemplateFieldValidation('beacons');
  const beacons = useAppSelector((s) => s.template.beacons);
  const errors = useAppSelector((s) => s.templateErrors);
  const beaconNameRef = useRef<HTMLDivElement>(null);
  const beaconProximityUuidRef = useRef<HTMLDivElement>(null);
  const beaconMajorRef = useRef<HTMLDivElement>(null);
  const beaconMinorRef = useRef<HTMLDivElement>(null);
  const beaconMessageRef = useRef<HTMLDivElement>(null);

  const { activeFieldRef, isFieldBeingEdited } = useFieldEditIndicator({
    prefix: 'beacons',
    index,
    fieldRefMap: {
      name: beaconNameRef,
      proximityUuid: beaconProximityUuidRef,
      major: beaconMajorRef,
      minor: beaconMinorRef,
      message: beaconMessageRef,
    },
  });

  const handleChange = (
    field: keyof BeaconInput,
    value: any,
    index: number
  ) => {
    const nextBeacons = beacons.slice();
    nextBeacons[index] = { ...nextBeacons[index], [field]: value };
    validateField(`${index}.${field}`, value, { beacons: nextBeacons });
    dispatch(setBeaconProp({ index, field, value }));
  };

  useEffect(() => {
    if (newlyAdded) {
      setIsOpen(true);
      dispatch(
        setBeaconProp({
          index,
          field: '_newlyAdded' as any,
          value: false,
        })
      );
    }
  }, [newlyAdded]);

  const beaconHasErrors = useMemo(() => {
    const errorKeys = Object.keys(errors);
    return errorKeys.some((key) => key.startsWith(`beacons.${index}.`));
  }, [errors, index]);

  useEffect(() => {
    if (beaconHasErrors) {
      setIsOpen(true);
    }
  }, [beaconHasErrors]);

  return (
    <Box p={3} border="1px solid" borderColor="lightGrey" borderRadius="md">
      <Flex
        alignItems="center"
        justify="space-between"
        cursor="pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Flex alignItems="center" gap={2}>
          <Text fontWeight="500">Beacon {index + 1}</Text>
          {beacon.name && (
            <Text fontSize="14px" fontWeight="600">
              | {beacon.name}
            </Text>
          )}
        </Flex>
        {isOpen ? (
          <ChevronDownIcon boxSize="20px" />
        ) : (
          <ChevronLeftIcon boxSize="20px" />
        )}
      </Flex>
      <Collapse in={isOpen}>
        <Flex direction="column" gap={4} mt={6}>
          {/* Beacon Name */}
          <FieldEditIndicator
            fieldRef={beaconNameRef}
            isFieldBeingEdited={isFieldBeingEdited}
            activeFieldRef={activeFieldRef}
            left="-55px"
            top="60px"
          >
            <FormControl
              isRequired
              isInvalid={!!errors[`beacons.${index}.name`]}
            >
              <LabelWithTooltip
                label="Beacon Name"
                tooltip="Internal name for this beacon. Used for display purposes only."
              />
              <Input
                value={beacon.name}
                onChange={(e) => handleChange('name', e.target.value, index)}
                onFocus={() => handleFocus(`beacons.${index}.name`)}
                onBlur={handleBlur}
                placeholder="Example: 'London Bakery Entrance'"
              />
              {errors[`beacons.${index}.name`] && (
                <FormErrorMessage
                  pl={2}
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                >
                  <CustomErrorIcon />
                  {errors[`beacons.${index}.name`]}
                </FormErrorMessage>
              )}
            </FormControl>
          </FieldEditIndicator>

          {/* Proximity UUID */}
          <FieldEditIndicator
            fieldRef={beaconProximityUuidRef}
            isFieldBeingEdited={isFieldBeingEdited}
            activeFieldRef={activeFieldRef}
            left="-55px"
            top="60px"
          >
            <FormControl
              isRequired
              isInvalid={!!errors[`beacons.${index}.proximityUuid`]}
            >
              <LabelWithTooltip
                label="Proximity UUID"
                tooltip="The beacon’s unique ID, used by devices to detect it. You'll usually get this value from your beacon provider or beacon management app."
              />
              <Input
                value={beacon.proximityUuid}
                onChange={(e) =>
                  handleChange('proximityUuid', e.target.value, index)
                }
                onFocus={() => handleFocus(`beacons.${index}.proximityUuid`)}
                onBlur={handleBlur}
                placeholder="Example: 'E2C56DB5-DFFB-48D2-B060-D0F5A71096E0'"
              />
              {errors[`beacons.${index}.proximityUuid`] && (
                <FormErrorMessage
                  pl={2}
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                >
                  <CustomErrorIcon />
                  {errors[`beacons.${index}.proximityUuid`]}
                </FormErrorMessage>
              )}
            </FormControl>
          </FieldEditIndicator>

          {/* Major ID */}
          <FieldEditIndicator
            fieldRef={beaconMajorRef}
            isFieldBeingEdited={isFieldBeingEdited}
            activeFieldRef={activeFieldRef}
            left="-55px"
            top="60px"
          >
            <FormControl isInvalid={!!errors[`beacons.${index}.major`]}>
              <LabelWithTooltip
                label="Major ID"
                tooltip="Used to group beacons under the same Proximity UUID. Optional, you can choose this value when configuring the beacon."
              />
              <Input
                value={beacon.major}
                type="number"
                onChange={(e) =>
                  handleChange(
                    'major',
                    e.target.value === '' ? null : Number(e.target.value),
                    index
                  )
                }
                onFocus={() => handleFocus(`beacons.${index}.major`)}
                onBlur={handleBlur}
                placeholder="Example: 1"
              />
              {errors[`beacons.${index}.major`] && (
                <FormErrorMessage
                  pl={2}
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                >
                  <CustomErrorIcon />
                  {errors[`beacons.${index}.major`]}
                </FormErrorMessage>
              )}
            </FormControl>
          </FieldEditIndicator>
          {/* Minor ID */}
          <FieldEditIndicator
            fieldRef={beaconMinorRef}
            isFieldBeingEdited={isFieldBeingEdited}
            activeFieldRef={activeFieldRef}
            left="-55px"
            top="60px"
          >
            <FormControl isInvalid={!!errors[`beacons.${index}.minor`]}>
              <LabelWithTooltip
                label="Minor ID"
                tooltip="Used to group beacons under the same Major ID. Optional, you can choose this value when configuring the beacon."
              />
              <Input
                value={beacon.minor}
                type="number"
                onChange={(e) =>
                  handleChange(
                    'minor',
                    e.target.value === '' ? null : Number(e.target.value),
                    index
                  )
                }
                onFocus={() => handleFocus(`beacons.${index}.minor`)}
                onBlur={handleBlur}
                placeholder="Example: 1"
              />
              {errors[`beacons.${index}.minor`] && (
                <FormErrorMessage
                  pl={2}
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                >
                  <CustomErrorIcon />
                  {errors[`beacons.${index}.minor`]}
                </FormErrorMessage>
              )}
            </FormControl>
          </FieldEditIndicator>

          {/* Notification Message */}
          <FieldEditIndicator
            fieldRef={beaconMessageRef}
            isFieldBeingEdited={isFieldBeingEdited}
            activeFieldRef={activeFieldRef}
            left="-55px"
            top="60px"
          >
            <FormControl isInvalid={!!errors[`beacons.${index}.message`]}>
              <LabelWithTooltip
                label="Notification Message"
                tooltip="The message users will see when they receive the push notification triggered by this beacon."
              />
              <Input
                value={beacon.message}
                onChange={(e) => handleChange('message', e.target.value, index)}
                onFocus={() => handleFocus(`beacons.${index}.message`)}
                onBlur={handleBlur}
                placeholder="Example: 'You've arrived — open your pass for quick access.'"
              />
              {errors[`beacons.${index}.message`] && (
                <FormErrorMessage
                  pl={2}
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                >
                  <CustomErrorIcon />
                  {errors[`beacons.${index}.message`]}
                </FormErrorMessage>
              )}
            </FormControl>
          </FieldEditIndicator>
          {/* Remove field link */}
          <Flex mt={4} alignItems="center" justify="flex-end" gap={2}>
            <Link
              variant="orange"
              fontSize="14px"
              onClick={() => dispatch(removeBeacon(index))}
            >
              Remove beacon trigger
            </Link>
          </Flex>
        </Flex>
      </Collapse>
    </Box>
  );
};

export default BeaconWrapper;
